import React from 'react';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';


/**
 * LaunchDarkly Provider component that initializes feature flags for the application.
 *
 * Renders children immediately without blocking, allowing LaunchDarkly to initialize
 * in the background. Since all flags default to off, the app can safely render
 * before initialization completes. Errors are logged silently without UI disruption.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - React children to render within the LaunchDarkly context
 * @param {Object} props.launchDarklyConfig - LaunchDarkly configuration object
 * @param {string} props.launchDarklyConfig.clientSideID - LaunchDarkly client-side ID (required)
 * @param {Object} [props.launchDarklyConfig.context] - Optional user context for LaunchDarkly
 * @param {Object} [props.launchDarklyConfig.options] - Optional SDK configuration options
 * @returns {React.ReactElement} The LaunchDarkly provider wrapper or direct children
 */
const LaunchDarklyProvider = ({ children, launchDarklyConfig }) => {
  const [LDProviderComponent, setLDProviderComponent] = useState(null);

  // Memoize the configuration to prevent unnecessary re-initializations
  const memoizedConfig = useMemo(
    () => ({
      clientSideID: launchDarklyConfig.clientSideID,
      context: launchDarklyConfig.context,
      options: launchDarklyConfig.options,
    }),
    [launchDarklyConfig.clientSideID, launchDarklyConfig.context, launchDarklyConfig.options],
  );

  const initializeLaunchDarkly = useCallback(async () => {
    try {
      if (!memoizedConfig.clientSideID) {
        throw new Error('LaunchDarkly client ID is required');
      }

      const provider = await asyncWithLDProvider({
        clientSideID: memoizedConfig.clientSideID,
        ...(memoizedConfig.context && { context: memoizedConfig.context }),
        options: {
          streaming: true,
          ...memoizedConfig.options,
        },
      });

      setLDProviderComponent(() => provider);
    } catch (err) {
      console.error('Failed to initialize LaunchDarkly:', err);
    }
  }, [memoizedConfig]);

  useEffect(() => {
    initializeLaunchDarkly();
  }, [initializeLaunchDarkly]);
  
  if (LDProviderComponent) {
    return <LDProviderComponent>{children}</LDProviderComponent>;
  }
  return children;
};

LaunchDarklyProvider.displayName = 'LaunchDarklyProvider';

LaunchDarklyProvider.propTypes = {
  children: PropTypes.node,
  launchDarklyConfig: PropTypes.shape({
    clientSideID: PropTypes.string.isRequired,
    context: PropTypes.object,
    options: PropTypes.object,
  }).isRequired,
};

export default LaunchDarklyProvider;